import React, { Component } from 'react';
import './AddressStreetView.css';
import StateConstants from 'src/states/StateContstants';
import ApiHandler from 'src/api/ApiHandler';
import EventAdaptingStateHandler from 'src/states/EventAdaptingStateHandler';

interface AddressStreetViewProps {
    address: string;
    /** Property/location id for cache key (required by get_address_image API). Omit to skip fetch until available. */
    locationId?: string;
    apiHandler: ApiHandler;
    stateHandler: EventAdaptingStateHandler;
    width?: number;
    height?: number;
    /** When true, skip valuation/bot check and fetch image by location only (e.g. for area pages) */
    locationOnly?: boolean;
}

interface AddressStreetViewState {
    imageSrc: string | null;
    isLoading: boolean;
    hasError: boolean;
}

class AddressStreetView extends Component<AddressStreetViewProps, AddressStreetViewState> {
    constructor(props: AddressStreetViewProps) {
        super(props);
        this.state = {
            imageSrc: null,
            isLoading: false,
            hasError: false
        };
    }

    async componentDidMount() {
        await this.fetchStreetViewImage();
    }

    async componentDidUpdate(prevProps: AddressStreetViewProps) {
        if (prevProps.address !== this.props.address) {
            await this.fetchStreetViewImage();
        }
    }

    async fetchStreetViewImage() {
        if (!this.props.locationOnly) {
            const valuation = this.props.stateHandler.get(StateConstants.VALUATION_STATE)
            const isGoogleBot = valuation?.["metadata"]?.["gb"]
            if (isGoogleBot) {
                return
            }
        }
        if (!this.props.address || !this.props.locationId || !this.props.apiHandler) {
            return
        }

        this.setState({ isLoading: true, hasError: false });

        const maxRetries = 2;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await this.props.apiHandler.getAddressImage({
                    location_string: this.props.address,
                    location_id: this.props.locationId,
                    width: this.props.width,
                    height: this.props.height
                });

                if (response?.raw_image && response.valid_image === true) {
                    const imageSrc = `data:image/jpeg;base64,${response.raw_image}`;
                    this.setState({
                        imageSrc,
                        isLoading: false,
                        hasError: false
                    });
                    return;
                } else {
                    this.setState({
                        imageSrc: null,
                        isLoading: false,
                        hasError: response?.raw_image && response.valid_image === false ? false : true
                    });
                    return;
                }
            } catch (error) {
                if (attempt < maxRetries) {
                    await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
                    continue;
                }
                console.error('Failed to fetch street view image:', error);
                this.setState({
                    imageSrc: null,
                    isLoading: false,
                    hasError: true
                });
            }
        }
    }

    render() {
        const { imageSrc, isLoading, hasError } = this.state;

        const placeholderHeight = this.props.height ?? 200;
        const showNothing = !imageSrc && !isLoading && !hasError;

        return (
            <div
                className={`address-street-view${showNothing ? " address-street-view-empty" : ""}`}
                style={{ aspectRatio: `${this.props.width ?? 480} / ${placeholderHeight}` }}
            >
                {isLoading && (
                    <div className="street-view-loading">
                        <span>Loading street view...</span>
                    </div>
                )}
                {!isLoading && imageSrc && (
                    <img
                        src={imageSrc}
                        alt={`Street view of ${this.props.address}`}
                        className="street-view-image"
                    />
                )}
                {!isLoading && !imageSrc && hasError && (
                    <div className="street-view-placeholder street-view-unavailable">
                        <span>Image unavailable</span>
                    </div>
                )}
            </div>
        );
    }
}

export default AddressStreetView;
